// AICA Notifications Dropdown Component
// Displays recent notifications in a dropdown menu
import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { m } from 'framer-motion';
import { cn, formatDateShort } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  ExternalLink,
  Bell,
} from 'lucide-react';
import { useRole } from '@/contexts/RoleContext';
import { useApiClient } from '@/hooks/useApiClient';
import { useNow } from '@/hooks/useNow';
import { Link } from '@tanstack/react-router';
import { logError } from '@/lib/logger';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  notificationRecentOptions,
  notificationRecentQueryKey,
} from '@/lib/query-options';
import type {
  NotificationCountResponse,
  NotificationEvent,
  NotificationsListResponse,
} from '@/lib/api';
import type { TFunction } from 'i18next';
import { isSameOriginUrl } from '@/lib/same-origin-url';

interface NotificationsDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRef?: React.RefObject<HTMLButtonElement | null>;
}

// =============================================
// Helper Functions (hoisted for performance)
// These don't depend on component state/props
// =============================================

function getSeverityColor(severity: NotificationEvent['severity']): string {
  switch (severity) {
    case 'critical':
      return 'text-danger';
    case 'high':
      return 'text-warning';
    case 'medium':
      return 'text-info';
    case 'low':
      return 'text-fg-muted';
    default:
      return 'text-fg-muted';
  }
}

function getSeverityIcon(severity: NotificationEvent['severity']): React.ReactNode {
  switch (severity) {
    case 'critical':
    case 'high':
      return <XCircle className="h-4 w-4" />;
    case 'medium':
      return <AlertCircle className="h-4 w-4" />;
    case 'low':
      return <Clock className="h-4 w-4" />;
    default:
      return <Bell className="h-4 w-4" />;
  }
}

function formatDate(dateStr: string, t: TFunction, nowMs: number): string {
  const date = new Date(dateStr);
  const diffMs = nowMs - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t('notifications.timeAgo.justNow');
  if (diffMins < 60) return t('notifications.timeAgo.minutesAgo', { count: diffMins });
  if (diffHours < 24) return t('notifications.timeAgo.hoursAgo', { count: diffHours });
  if (diffDays < 7) return t('notifications.timeAgo.daysAgo', { count: diffDays });
  return formatDateShort(date);
}

// =============================================
// Component
// =============================================

export function NotificationsDropdown({
  isOpen,
  onClose,
  anchorRef,
}: NotificationsDropdownProps) {
  const [position, setPosition] = useState({ bottom: 0, left: 0 });
  const { selectedOrgId } = useRole();
  const { apiClient } = useApiClient();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // Keeps "N minutes ago" labels fresh — nothing else re-renders an idle
  // dropdown since the token-refresh render storm was eliminated.
  const now = useNow();
  const notificationsQuery = useQuery({
    ...notificationRecentOptions(apiClient, selectedOrgId),
    enabled: isOpen && !!selectedOrgId,
  });
  const notifications = notificationsQuery.data?.items ?? [];
  const isLoading = notificationsQuery.isLoading && notifications.length === 0;

  // Calculate dropdown position based on anchor button
  useEffect(() => {
    if (isOpen && anchorRef?.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      // Dropdown opens UPWARD from the bottom-positioned bell icon
      // bottom = distance from viewport bottom to button top
      setPosition({
        bottom: window.innerHeight - rect.top + 8,
        left: rect.left,
      });
    }
  }, [isOpen, anchorRef]);

  useEffect(() => {
    if (!notificationsQuery.error) {
      return;
    }

    logError(
      'notifications.fetch_failed',
      { component: 'NotificationsDropdown', orgId: selectedOrgId ?? undefined },
      notificationsQuery.error instanceof Error ? notificationsQuery.error : undefined
    );
  }, [notificationsQuery.error, notificationsQuery.errorUpdatedAt, selectedOrgId]);

  // Mark notification as read and update query cache without full refetch.
  const handleMarkAsRead = useCallback(
    async (notificationId: string) => {
      if (!selectedOrgId) return;

      try {
        await apiClient.notifications.markNotificationRead(notificationId);

        queryClient.setQueryData<NotificationsListResponse>(
          notificationRecentQueryKey(selectedOrgId),
          (previous) =>
            previous
              ? {
                  ...previous,
                  items: previous.items.map((notification) =>
                    notification.id === notificationId
                      ? { ...notification, status: 'read' }
                      : notification
                  ),
                }
              : previous
        );

        queryClient.setQueryData<NotificationCountResponse>(
          ['notifications', 'count', selectedOrgId],
          (previous) =>
            previous ? { ...previous, count: Math.max(0, previous.count - 1) } : previous
        );
      } catch (err) {
        logError(
          'notifications.mark_read_failed',
          { component: 'NotificationsDropdown', notificationId },
          err instanceof Error ? err : undefined
        );
      }
    },
    [selectedOrgId, apiClient, queryClient]
  );

  // Use Portal to render outside of stacking context.
  // No AnimatePresence here on purpose: closing often happens in the same
  // click as a router navigation (the "view all" link), and TanStack Router
  // wraps navigation in startTransition — that concurrent update can freeze
  // an in-flight framer-motion exit animation, leaving the dropdown stuck
  // half-faded over the page. Plain conditional rendering unmounts
  // deterministically; only the entry animation remains.
  return createPortal(
    isOpen ? (
      <>
        {/* Backdrop */}
        <m.div
          data-testid="notifications-backdrop"
          className="fixed inset-0 z-[9998]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.15 }}
          onClick={onClose}
          aria-hidden="true"
        />

        {/* Dropdown - fixed position with portal to escape stacking context */}
        <m.div
          data-testid="notifications-dropdown"
          className={cn(
            'fixed',
            'z-[9999]',
            'w-96 max-h-[32rem] flex flex-col',
            'rounded-lg border border-border bg-bg shadow-lg overflow-hidden'
          )}
          style={{
            bottom: position.bottom,
            left: position.left,
          }}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="notifications-button"
        >
          {/* Header */}
          <div
            data-testid="notifications-header"
            className="flex items-center justify-between border-b border-border px-4 py-3 shrink-0"
          >
            <h3 className="text-sm font-semibold text-fg">{t('notifications.title')}</h3>
            <Link
              data-testid="notifications-view-all-link"
              to="/notifications"
              className={cn(
                'text-xs text-primary hover:text-primary-hover transition-all duration-150',
                'flex items-center gap-1'
              )}
              onClick={onClose}
            >
              {t('notifications.viewAll')}
              <ExternalLink className="h-3 w-3" />
            </Link>
          </div>

          {/* Content */}
          <div
            data-testid="notifications-content"
            className="flex-1 min-h-0 overflow-y-auto"
          >
            {isLoading ? (
              <div
                data-testid="notifications-loading"
                className="flex items-center justify-center py-8"
              >
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : notifications.length === 0 ? (
              <div
                data-testid="notifications-empty"
                className="flex flex-col items-center justify-center py-8 px-4 text-center"
              >
                <CheckCircle className="h-12 w-12 text-success mb-3" />
                <p className="text-sm font-medium text-fg">
                  {t('notifications.allCaughtUp')}
                </p>
                <p className="text-xs text-fg-muted mt-1">
                  {t('notifications.noNotifications')}
                </p>
              </div>
            ) : (
              <div
                data-testid="notifications-list"
                className="divide-y divide-bg-elevated"
              >
                {notifications.map((notification) => (
                  <Button
                    key={notification.id}
                    variant="ghost"
                    size="sm"
                    data-testid={`notification-item-${notification.id}`}
                    className={cn(
                      'w-full block px-4 py-3 hover:bg-bg-elevated transition-all duration-150 text-left',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                      notification.status === 'pending' && 'bg-bg-subtle'
                    )}
                    onClick={async () => {
                      // Mark as read if pending
                      if (notification.status === 'pending') {
                        await handleMarkAsRead(notification.id);
                      }

                      // Navigate if action URL exists
                      if (
                        notification.actionUrl &&
                        isSameOriginUrl(notification.actionUrl)
                      ) {
                        window.location.href = notification.actionUrl;
                      }

                      onClose();
                    }}
                    role="menuitem"
                  >
                    <div className="flex items-start gap-2">
                      {/* Unread indicator - LEFT side */}
                      <div className="mt-1.5 w-2 shrink-0 flex justify-center">
                        {notification.status === 'pending' && (
                          <span className="h-2 w-2 rounded-full bg-primary" />
                        )}
                      </div>

                      {/* Severity Icon */}
                      <div
                        className={cn(
                          'mt-0.5 shrink-0',
                          getSeverityColor(notification.severity)
                        )}
                      >
                        {getSeverityIcon(notification.severity)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-fg truncate">
                            {notification.title}
                          </span>
                        </div>

                        <p className="text-xs text-fg-muted line-clamp-2">
                          {notification.message}
                        </p>

                        <div className="flex items-center gap-2 mt-1.5">
                          <span
                            className={cn(
                              'text-xs px-1.5 py-0.5 rounded',
                              'bg-bg-elevated border border-border',
                              getSeverityColor(notification.severity)
                            )}
                          >
                            {notification.severity.toUpperCase()}
                          </span>
                          <span className="text-xs text-fg-muted">
                            {formatDate(notification.createdAt, t, now)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div
              data-testid="notifications-footer"
              className="border-t border-border px-4 py-2 shrink-0"
            >
              <Link
                data-testid="notifications-footer-link"
                to="/notifications"
                className={cn(
                  'block text-center text-xs text-primary hover:text-primary-hover',
                  'py-1 transition-all duration-150'
                )}
                onClick={onClose}
              >
                {t('notifications.viewAllCount', { count: notifications.length })}
              </Link>
            </div>
          )}
        </m.div>
      </>
    ) : null,
    document.body
  );
}
